import { createSlice } from '@reduxjs/toolkit';
import { ReduxStatus } from '../../../../shared/types';
import {
  createCommunity,
  getBasicCommunities,
  getCommunityDetails,
  replaceAPNPush,
  replaceGCMPush,
  buildCommunityFeed,
  sendCommunitySummary,
} from './operators';
import { CommunityModel } from '../models/Community.model';
import { BasicCommunityModel } from '../models/BasicCommunity.model';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';

export interface CommunityState {
  communities: CommunityModel[];
  paginationMeta: PaginationMetaModel;
  basicCommunities?: BasicCommunityModel[];
  community: CommunityModel | null;
  getCommunityDetailsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunitiesStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getBasicCommunitiesStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  createCommunityStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  replaceCommunityPushStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  buildCommunityPushStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  rebuildCommunityFeedStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  sendCommunitySummaryStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: CommunityState = {
  communities: [],
  paginationMeta: {
    current: 0,
    pages: 0,
    total: 0,
  },
  basicCommunities: undefined,
  community: null,
  getCommunityDetailsStatus: {
    status: 'idle',
    error: null,
  },
  createCommunityStatus: {
    status: 'idle',
    error: null,
  },
  getBasicCommunitiesStatus: {
    status: 'idle',
    error: null,
  },
  getCommunitiesStatus: {
    status: 'idle',
    error: null,
  },
  replaceCommunityPushStatus: {
    status: 'idle',
    error: null,
  },
  buildCommunityPushStatus: {
    status: 'idle',
    error: null,
  },
  rebuildCommunityFeedStatus: {
    status: 'idle',
    error: null,
  },
  sendCommunitySummaryStatus: {
    status: 'idle',
    error: null,
  },
};

const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createCommunity.pending, (state) => {
      state.createCommunityStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(createCommunity.fulfilled, (state) => {
      state.createCommunityStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(createCommunity.rejected, (state, { payload }) => {
      state.createCommunityStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to create community',
      };
    });
    builder.addCase(getBasicCommunities.pending, (state) => {
      state.getBasicCommunitiesStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getBasicCommunities.fulfilled, (state, { payload }) => {
      state.getBasicCommunitiesStatus = {
        status: 'success',
        error: null,
      };
      state.basicCommunities = payload;
    });
    builder.addCase(getBasicCommunities.rejected, (state, { payload }) => {
      state.getBasicCommunitiesStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch basic communities',
      };
    });
    builder.addCase(getCommunityDetails.pending, (state) => {
      state.getCommunityDetailsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunityDetails.fulfilled, (state, { payload }) => {
      state.getCommunityDetailsStatus = {
        status: 'success',
        error: null,
      };
      state.community = payload;
    });
    builder.addCase(getCommunityDetails.rejected, (state, { payload }) => {
      state.getCommunityDetailsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community details',
      };
    });
    builder.addCase(replaceAPNPush.pending, (state) => {
      state.replaceCommunityPushStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(replaceGCMPush.pending, (state) => {
      state.replaceCommunityPushStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(replaceAPNPush.fulfilled, (state) => {
      state.replaceCommunityPushStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(replaceGCMPush.fulfilled, (state) => {
      state.replaceCommunityPushStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(replaceAPNPush.rejected, (state, { payload }) => {
      state.replaceCommunityPushStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community details',
      };
    });
    builder.addCase(replaceGCMPush.rejected, (state, { payload }) => {
      state.replaceCommunityPushStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community details',
      };
    });
    builder.addCase(buildCommunityFeed.pending, (state) => {
      state.rebuildCommunityFeedStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(buildCommunityFeed.fulfilled, (state) => {
      state.rebuildCommunityFeedStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(buildCommunityFeed.rejected, (state, { payload }) => {
      state.rebuildCommunityFeedStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to build community feed',
      };
    });
    builder.addCase(sendCommunitySummary.pending, (state) => {
      state.sendCommunitySummaryStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(sendCommunitySummary.fulfilled, (state) => {
      state.sendCommunitySummaryStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(sendCommunitySummary.rejected, (state, { payload }) => {
      state.sendCommunitySummaryStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to send community summary',
      };
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export default communitySlice.reducer;
